import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

/** At most this many ids per `getByIds` call. */
export const BY_IDS_BATCH = 100;

export interface UsePickerItemsByIdOptions<TItem> {
  /** Ids to show whose rows the picker does not have yet. */
  missing: readonly string[];
  getByIds: ((ids: readonly string[], signal: AbortSignal) => Promise<TItem[]>) | undefined;
  getId: (item: TItem) => string;
  enabled: boolean;
}

/**
 * Rows the Selected tab loads by id (initial and pasted ids that never showed in the list). Rows
 * are kept for the life of the picker, so a row does not flash when the missing set shrinks.
 */
export function usePickerItemsById<TItem>({
  missing,
  getByIds,
  getId,
  enabled,
}: UsePickerItemsByIdOptions<TItem>) {
  const scope = React.useId();
  const [fetched, setFetched] = React.useState<ReadonlyMap<string, TItem>>(() => new Map());
  // Ids asked for that came back without a row: deleted, or not the tenant's.
  const [unknown, setUnknown] = React.useState<ReadonlySet<string>>(() => new Set());
  const wanted = missing
    .filter((id) => !fetched.has(id) && !unknown.has(id))
    .slice(0, BY_IDS_BATCH);
  // `scope` ties the key to this picker; the functions are the caller's, not inputs.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery({
    queryKey: ['admin-kit', 'resource-picker', scope, 'by-ids', wanted],
    queryFn: async ({ signal }) => {
      const rows = getByIds ? await getByIds(wanted, signal) : [];
      const found = new Set(rows.map(getId));
      setFetched((prev) => {
        const next = new Map(prev);
        for (const row of rows) next.set(getId(row), row);
        return next;
      });
      const gone = wanted.filter((id) => !found.has(id));
      if (gone.length > 0) setUnknown((prev) => new Set([...prev, ...gone]));
      return rows;
    },
    enabled: enabled && getByIds !== undefined && wanted.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
  });

  return {
    fetched,
    /** Ids that `getByIds` did not return. */
    unknown,
    loading: query.isFetching,
    error: query.isError ? query.error : null,
    retry: () => void query.refetch(),
  };
}
